import {
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    Output,
    SimpleChanges,
} from '@angular/core';
import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs';

import { AUTOCOMPLETER_STATE_SLICE_KEY } from 'SearchApp/ts/features/base-autocomplete/state/autocompleter.reducer';

import { ImpersonationMenuUser } from 'AccountsApp/ts/interfaces/impersonation.interface';
import { AutocompleteStore } from 'SearchApp/ts/features/base-autocomplete/state/interfaces/autocomplete-store.interface';
import { ImpersonationPermissionAutocompleteResult } from 'SearchApp/ts/interfaces/autocomplete-result.interface';

import { UrlsService } from 'Angular/services/core/urls.service';

import { abbreviateUserDisplayName } from 'Angular/features/components/navigation/utils';
import { SearchResultsBaseDirective } from 'SearchApp/ts/features/base-autocomplete/directives/search-results.directive';

/**
 * Impersonation Target User List Component
 *
 * Displays and manages a list of users that can be impersonated. The list can be the
 * initial target user list OR autocompleter results.
 */
@Component({
    standalone: false,
    selector: 'ycn-impersonation-target-user-list',
    templateUrl: './impersonation-target-user-list.template.html',
})
export class ImpersonationTargetUserListComponent extends SearchResultsBaseDirective implements OnChanges, OnDestroy {
    @Input() initialTargetUserList: ImpersonationMenuUser[] = [];

    @Input() currentTargetUser: ImpersonationMenuUser | null = null;

    @Output() setCurrentTargetUserHandler = new EventEmitter<ImpersonationMenuUser>();

    autocompleterTargetUserList: ImpersonationPermissionAutocompleteResult[] = [];

    showAutocompleterResults: boolean = false;

    private storeSubscription: Subscription;

    constructor(
        private changeDetector: ChangeDetectorRef,
        urlsService: UrlsService,
        readonly store: Store<{ autocompleter: AutocompleteStore }>
    ) {
        super(urlsService, store);
        this.storeSubscription = this.store.select(AUTOCOMPLETER_STATE_SLICE_KEY).subscribe(this.mapStateToComponent);
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.currentTargetUser?.currentValue) {
            this.changeDetector.detectChanges();
        }
    }

    ngOnDestroy(): void {
        this.storeSubscription.unsubscribe();
    }

    mapStateToComponent = (state: AutocompleteStore): void => {
        const autocompleterState = state[this.autocompleterId];
        if (!autocompleterState) {
            return;
        }
        this.inputText = autocompleterState.inputText;
        this.autocompleterTargetUserList =
            autocompleterState.results as unknown as ImpersonationPermissionAutocompleteResult[];

        // We don't use autocompleterState.showResults because we want to show results when there's
        // input text and the field is focused, even if the results array is empty.
        this.showAutocompleterResults = this.inputText.length > 0 && !autocompleterState.blurred;
    };

    setCurrentTargetUser(user: ImpersonationMenuUser): void {
        this.setCurrentTargetUserHandler.emit(user);
    }

    setCurrentTargetUserAutocomplete(result: ImpersonationPermissionAutocompleteResult): void {
        // dispatch action to set selected result in store
        this.clickResult();

        const currentTargetUser = {
            email: result.target_user_email,
            first_name: result.target_user_first_name,
            last_name: result.target_user_last_name,
        };
        this.setCurrentTargetUserHandler.emit(currentTargetUser);
    }

    protected getUserDisplayName(firstName: string, lastName: string): string {
        return abbreviateUserDisplayName(firstName, lastName, 26);
    }
}
